<template>
  <div>
    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="headline pa-4" style="flex-wrap: nowrap">
          <v-icon size="40" color="error" class="mr-4 flex-shrink-0"
            >mdi mdi-alert</v-icon
          >
          <div class="flex-shrink-1" style="min-width: 0">
            <div class="text-truncate">Stop — Bulk Text Messages Detected</div>
            <div class="subtitle-2 font-weight-regular text--secondary">
              A change in your FMS may send texts to your tenants
            </div>
          </div>
          <v-chip
            v-if="queueCount > 1"
            small
            color="primary"
            outlined
            class="ml-2 flex-shrink-0"
          >
            {{ currentIndex }} of {{ queueCount }}
          </v-chip>
          <v-btn
            v-if="hasPrevious"
            text
            color="primary"
            class="ml-2 flex-shrink-0"
            :disabled="loading"
            @click="handlePrevious"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Previous
          </v-btn>
          <v-btn
            v-if="hasNext"
            text
            color="primary"
            class="ml-1 flex-shrink-0"
            :disabled="loading"
            @click="handleNext"
          >
            <v-icon left>mdi-arrow-right</v-icon>
            Next
          </v-btn>
          <v-spacer />
          <v-btn icon small class="ml-2 flex-shrink-0" @click="close">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="bulkNotification">
            <!-- FMS change explanation -->
            <v-alert color="orange" class="mb-4 caption font-weight-bold" text>
              <span class="body-2">
                StorageDefender detected a change from your Facility Management
                Software (e.g. Sitelink, StorEdge, SSM). This usually happens
                when someone edits a <strong>unit type</strong> or
                <strong>service charge</strong> in the FMS.
              </span>
            </v-alert>

            <v-row class="mb-4">
              <v-col cols="12">
                <!-- <div class="subtitle-1 font-weight-medium mb-2  d-flex">
                  Operation Summary

                  <v-chip :color="statusColor" small outlined class="mb-2 ml-2" >{{
                  statusText
                }}</v-chip>
                </div>      
                <div class="body-2 text--secondary mb-2">
                  <strong>Operation Type:</strong>
                  {{ getOperationType(bulkNotification) }}
                </div>
                <div class="body-2 text--secondary mb-2">
                  <strong>Total Units:</strong>
                  {{ notifications.length || "Unknown" }}
                </div>
                <div class="body-2 text--secondary mb-2">
                  <strong>Created:</strong>
                  {{ formatDateTime(bulkNotification.insertedAt) }}
                </div> -->

                <!-- Show workflow status if exists -->
                <!-- <div v-if="bulkNotification.workflow" class="mt-3">
                  <div
                    v-if="bulkNotification.workflow.approved_at"
                    class="body-2"
                  >
                    <v-chip color="success" small class="mr-2">
                      <v-icon small left>mdi-check</v-icon>
                      Approved
                    </v-chip>
                    <span class="text--secondary">
                      {{
                        formatDateTime(bulkNotification.workflow.approved_at)
                      }}
                      <span v-if="bulkNotification.workflow.approved_by">
                        by {{ bulkNotification.workflow.approved_by }}
                      </span>
                    </span>
                  </div>

                  <div
                    v-else-if="bulkNotification.workflow.rejected_at"
                    class="body-2"
                  >
                    <v-chip color="error" small class="mr-2">
                      <v-icon small left>mdi-close</v-icon>
                      Rejected
                    </v-chip>
                    <span class="text--secondary">
                      {{
                        formatDateTime(bulkNotification.workflow.rejected_at)
                      }}
                      <span v-if="bulkNotification.workflow.rejected_by">
                        by {{ bulkNotification.workflow.rejected_by }}
                      </span>
                    </span>
                    <div
                      v-if="bulkNotification.workflow.reason"
                      class="mt-2 pa-2"
                      style="background-color: #ffebee; border-radius: 4px"
                    >
                      <strong class="error--text">Rejection Reason:</strong>
                      <div class="mt-1">
                        {{ bulkNotification.workflow.reason }}
                      </div>
                    </div>
                  </div>
                </div> -->

                <v-alert color="error" outlined class="text-center my-3">
                  <div
                    class="subtitle-1 font-weight-bold error--text text-uppercase mb-1"
                  >
                    If you approve, this will send
                  </div>
                  <div class="display-1 error--text font-weight-black">
                    {{ notifications.length }}
                  </div>
                  <div class="subtitle-1 font-weight-bold error--text">
                    Text message to tenant(s) right now
                  </div>
                </v-alert>
              </v-col>
            </v-row>

            <v-divider class="mb-4" />
            <v-row>
              <v-col cols="12">
                <div class="subtitle-1 font-weight-medium mb-3">
                  Affected Units ({{ notifications.length }})
                </div>

                <v-data-table
                  :headers="unitHeaders"
                  :items="notifications"
                  :items-per-page="10"
                  :footer-props="{ 'items-per-page-options': [5, 10, 25, -1] }"
                  dense
                >
                  <template #item.user="{ item }">
                    <div class="d-flex flex-column">
                      <span class="font-weight-medium"
                        >{{ item.user?.firstName }}
                        {{ item.user?.lastName }}</span
                      >
                    </div>
                  </template>

                  <template #item.organization="{ item }">
                    <div class="d-flex flex-column">
                      <span class="font-weight-medium">{{
                        getUnitName(item)
                      }}</span>
                      <span class="text-caption text--secondary">{{
                        item.organization?.name
                      }}</span>
                    </div>
                  </template>

                  <template #item.workflowType="{ item }">
                    <v-chip
                      :color="getWorkflowTypeColor(item.workflowType)"
                      x-small
                      outlined
                    >
                      {{ formatWorkflowType(item.workflowType) }}
                    </v-chip>
                  </template>

                  <template #item.status="{ item }">
                    <v-chip
                      :color="getNotificationStatusColor(item.status)"
                      x-small
                      outlined
                    >
                      {{ getNotificationStatusText(item.status) }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>

            <!-- Not sure? guidance box -->
            <v-alert
              outlined
              color=""
              class="mt-2"
              icon="mdi-information-outline"
            >
              <span class="body-2">
                <strong>Not sure?</strong> Contact your district or regional
                manager to confirm whether this FMS change was intentional
                before approving. When in doubt, always reject — it will
                <strong>not</strong> affect your tenants.
              </span>
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions v-if="shouldShowActions">
          <v-spacer />
          <v-btn
            color="success"
            :loading="loading"
            :disabled="!canTakeAction"
            @click="handleReject"
          >
            Reject — no texts will be sent
          </v-btn>
          <v-btn
            color="error"
            outlined
            :loading="loading"
            :disabled="!canTakeAction"
            @click="handleApprove"
          >
            Approve & send texts
          </v-btn>
        </v-card-actions>
        <div
          v-if="shouldShowActions"
          class="text-center caption text--secondary pb-3"
        >
          Rejecting is always the safe choice. No tenant data will be changed.
        </div>
      </v-card>
    </v-dialog>

    <!-- Approve Confirmation Dialog -->
    <v-dialog v-model="approveDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Confirm Approval
        </v-card-title>

        <v-card-text>
          <v-alert color="error" text class="mb-4">
            <span class="body-2 font-weight-bold">
              You are about to send
              <strong>{{ notifications.length }}</strong> text message{{
                notifications.length !== 1 ? "s" : ""
              }}
              to tenants. This action cannot be undone.
            </span>
          </v-alert>
          <p class="body-2">
            Are you sure you want to approve and send these text message(s)?
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="approving" @click="cancelApprove">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="approving" @click="confirmApprove">
            <v-icon left>mdi-check</v-icon>
            Yes, Approve & Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import {
  templateLabel,
  getBulkNotificationStatusColor,
  getBulkNotificationStatusText,
  getNotificationStatusColor,
  getNotificationStatusText,
} from "./helper";
import { titleCase } from "@tod-ui/helpers/strings";

export default {
  name: "DialogBulkOperationDetails",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    bulkNotification: {
      type: Object,
      required: false,
      default: null,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    hasNext: {
      type: Boolean,
      default: false,
    },
    hasPrevious: {
      type: Boolean,
      default: false,
    },
    queueCount: {
      type: Number,
      default: 1,
    },
    currentIndex: {
      type: Number,
      default: 1,
    },
  },
  emits: ["input", "approve", "reject", "close", "next", "previous"],
  setup(props, { emit }) {
    const loading = ref(false);

    const dialog = computed({
      get: () => props.value,
      set: (value) => emit("input", value),
    });

    const notifications = computed(() => {
      if (
        props.bulkNotification?.user &&
        props.bulkNotification?.organization &&
        !props.bulkNotification?.notifications &&
        !props.bulkNotification?.workflow
      ) {
        return [
          {
            ...props.bulkNotification,
            user: {
              firstName: props.bulkNotification.user?.firstName || "Unknown",
              lastName: props.bulkNotification.user?.lastName || "Tenant",
              id: props.bulkNotification.user?.id || "unknown",
            },
          },
        ];
      }

      const traditionalNotifications =
        props.bulkNotification?.notifications || [];
      if (traditionalNotifications.length > 0) {
        // Filter out SMS notifications
        return traditionalNotifications.filter((notification) => {
          const workflowType =
            notification.workflowType ||
            notification.workflow_type ||
            notification.name ||
            "";
          return (
            !workflowType.toLowerCase().includes("sms") &&
            !workflowType.toLowerCase().includes("send_move_in_sms") &&
            !workflowType.toLowerCase().includes("send_move_out_sms")
          );
        });
      }

      try {
        const workflowData = props.bulkNotification?.workflow;
        if (!workflowData) {
          if (
            props.bulkNotification?.user &&
            props.bulkNotification?.organization
          ) {
            return [props.bulkNotification];
          }

          return [
            {
              id: "mock-1",
              status: "pending",
              user: {
                firstName: "Unknown",
                lastName: "Tenant",
                id: "unknown",
              },
              organization: {
                id: props.bulkNotification?.organization?.id || "unknown",
                name:
                  props.bulkNotification?.organization?.name ||
                  "Unknown Facility",
              },
              triggeringEntities: {
                name: "Unknown Unit",
              },
              workflowType: props.bulkNotification?.name || "Unknown Operation",
            },
          ];
        }

        const parsedWorkflow =
          typeof workflowData === "string"
            ? JSON.parse(workflowData)
            : workflowData;

        if (
          !parsedWorkflow.workflows ||
          !Array.isArray(parsedWorkflow.workflows)
        ) {
          return [];
        }

        return parsedWorkflow.workflows
          .filter((workflow) => {
            // Filter out SMS notifications
            const workflowType =
              workflow.workflow_type || workflow.workflow || "";
            return (
              !workflowType.toLowerCase().includes("sms") &&
              !workflowType.toLowerCase().includes("send_move_in_sms") &&
              !workflowType.toLowerCase().includes("send_move_out_sms")
            );
          })
          .map((workflow, index) => {
            const tenantId = workflow.tenant_id;
            const tenantName =
              workflow.tenant_name ||
              (workflow.tenant_first_name && workflow.tenant_last_name
                ? `${workflow.tenant_first_name} ${workflow.tenant_last_name}`
                : workflow.tenant_first_name || `Tenant ${index + 1}`);

            const unitId = workflow.unit_id;
            const unitNumber =
              workflow.unit_number || workflow.unit_name || `Unit ${index + 1}`;

            const organizationId = workflow.organization_id;

            return {
              id: `workflow-${index}`,
              status: workflow.status || "pending_bulk_approval",
              user: {
                firstName: workflow.tenant_first_name || tenantName,
                lastName: workflow.tenant_last_name || "",
                id: tenantId || `tenant-${index}`,
              },
              organization: {
                id: organizationId || `org-${index}`,
                name: parsedWorkflow.facility_name || "Unknown Facility",
              },
              triggeringEntities: {
                name: unitNumber,
              },
              workflowType:
                workflow.workflow_type ||
                workflow.workflow ||
                "Unknown Operation",
            };
          });
      } catch (error) {
        console.error("Error parsing workflow data:", error);
        return [];
      }
    });

    const templateName = computed(() => {
      return (
        templateLabel(props.bulkNotification?.type) ||
        props.bulkNotification?.name ||
        "Bulk Operation"
      );
    });

    const statusColor = computed(() => {
      return getBulkNotificationStatusColor(props.bulkNotification?.status);
    });

    const statusText = computed(() => {
      return getBulkNotificationStatusText(props.bulkNotification?.status);
    });

    const canTakeAction = computed(() => {
      return props.bulkNotification?.status === "published";
    });

    const shouldShowActions = computed(() => {
      if (!props.showActions) return false;

      const status = props.bulkNotification?.status;
      if (status === "dismissed" || status === "resolved") {
        return false;
      }

      return status === "published";
    });

    const unitHeaders = [
      { text: "Tenant Name", value: "user", sortable: false },
      { text: "Unit / Facility", value: "organization", sortable: false },
      { text: "Operation Type", value: "workflowType", sortable: false },
      { text: "Status", value: "status", sortable: false, align: "center" },
    ];

    const getOperationType = (bulkNotification) => {
      if (!bulkNotification?.name) return "Unknown Operation";

      if (bulkNotification.name.includes("move_in")) {
        return "Move-In Operations";
      } else if (bulkNotification.name.includes("move_out")) {
        return "Move-Out Operations";
      } else if (bulkNotification.name.includes("mixed")) {
        return "Mixed Operations (Move-In/Move-Out)";
      }

      return "Bulk Text Message Defender";
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return "";
      return new Date(dateTime).toLocaleString();
    };

    const getUnitName = (notification) => {
      try {
        const entities =
          typeof notification.triggeringEntities === "string"
            ? JSON.parse(notification.triggeringEntities)
            : notification.triggeringEntities;

        if (Array.isArray(entities)) {
          const unitEntity = entities.find(
            (entity) => entity.name || entity.unit_name
          );
          if (unitEntity) {
            return unitEntity.name || unitEntity.unit_name;
          }
        } else if (entities && (entities.name || entities.unit_name)) {
          return entities.name || entities.unit_name;
        }

        return notification.organization?.name || "Unknown Unit";
      } catch (error) {
        console.error("Error parsing triggering entities:", error);
        return notification.organization?.name || "Unknown Unit";
      }
    };

    const getNotificationStatusColorWrapper = (status) => {
      return getNotificationStatusColor(status, props.bulkNotification?.status);
    };

    const getNotificationStatusTextWrapper = (status) => {
      return getNotificationStatusText(status, props.bulkNotification?.status);
    };

    const formatWorkflowType = (workflowType) => {
      if (!workflowType) return "Unknown";

      switch (workflowType) {
        case "move_in":
          return "Move In";
        case "move_out":
          return "Move Out";
        case "move_in_completed_with_sd":
          return "Move In";
        case "move_in_completed_without_device":
          return "Move In";
        case "move_out_completed":
          return "Move Out";
        default:
          // Convert snake_case to Title Case
          return titleCase(workflowType.replace(/_/g, " "));
      }
    };

    const getWorkflowTypeColor = (workflowType) => {
      if (!workflowType) return "grey";

      if (workflowType.includes("move_in")) {
        return "green";
      } else if (workflowType.includes("move_out")) {
        return "orange";
      } else {
        return "blue";
      }
    };

    const close = () => {
      emit("close");
    };

    const approveDialog = ref(false);
    const approving = ref(false);

    const handleApprove = () => {
      approveDialog.value = true;
    };

    const confirmApprove = async () => {
      approving.value = true;
      loading.value = true;
      try {
        emit("approve", props.bulkNotification);
        approveDialog.value = false;
      } finally {
        approving.value = false;
        loading.value = false;
      }
    };

    const cancelApprove = () => {
      approveDialog.value = false;
    };

    const rejecting = ref(false);

    const handleReject = async () => {
      rejecting.value = true;
      try {
        emit("reject", props.bulkNotification, null);
      } finally {
        rejecting.value = false;
      }
    };

    const handleNext = () => {
      emit("next");
    };

    const handlePrevious = () => {
      emit("previous");
    };

    return {
      dialog,
      loading,
      notifications,
      templateName,
      statusColor,
      statusText,
      canTakeAction,
      shouldShowActions,
      unitHeaders,
      approveDialog,
      approving,
      rejecting,
      formatDateTime,
      getUnitName,
      getNotificationStatusColor: getNotificationStatusColorWrapper,
      getNotificationStatusText: getNotificationStatusTextWrapper,
      formatWorkflowType,
      getWorkflowTypeColor,
      getOperationType,
      close,
      handleApprove,
      handleReject,
      confirmApprove,
      cancelApprove,
      handleNext,
      handlePrevious,
      titleCase,
    };
  },
};
</script>

<style scoped>
.headline {
  word-break: break-word;
}

.v-data-table {
  border: 1px solid #e0e0e0;
}
</style>
